<script lang="ts">
  // Linha de tabela estilo cartoon (Felipe 2026-05-26 inspirado em mockup
  // de app de torneios de poker). Fundo creme alternado, header roxo na
  // TournamentsTab, botoes verdes "puffy" com gradient.
  import type { Tournament } from '../../stores/tournaments';
  import { registerTournament, unregisterTournament, startTournament, isAdmin } from '../../stores/tournaments';
  import { userId, isAuthenticated } from '../../stores/auth';
  import { openLogin } from '../../stores/uiState';
  import { Play, Clock } from 'lucide-svelte';

  type Props = { tournament: Tournament; index?: number };
  let { tournament: t, index = 0 }: Props = $props();

  let isRegistered = $derived(!!t.registrations?.includes($userId));
  let regCount = $derived(t.registrations?.length ?? 0);
  // Player comum NAO ve contagem (Leonardo 2026-05-31: "1/32 delata bot").
  // So admin ve "X/Y inscritos" pra debug.
  let isFull = $derived(regCount >= t.size);
  let busy = $state(false);
  let starting = $state(false);

  let startsAtDate = $derived(new Date(t.starts_at));
  let timeLabel = $derived.by(() => {
    const d = startsAtDate;
    const now = new Date();
    const diffMin = Math.round((d.getTime() - now.getTime()) / 60000);
    if (diffMin < 0) return { primary: 'Agora', secondary: '' };
    if (diffMin < 60) return { primary: `Em ${diffMin} min`, secondary: '' };
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return { primary: `${dd}/${mm}`, secondary: `${hh}:${mi}` };
  });

  async function toggleReg() {
    if (busy) return;
    // Nao logado: abre o modal de login em vez de inscrever (Felipe
    // 2026-06-01: login so ao tentar entrar num campeonato).
    if (!$isAuthenticated) {
      openLogin();
      return;
    }
    busy = true;
    if (isRegistered) await unregisterTournament(t.id);
    else await registerTournament(t.id);
    busy = false;
  }

  async function handleStart() {
    if (starting) return;
    if (!confirm(`Iniciar torneio agora? Vagas restantes serão preenchidas com bots.`)) return;
    starting = true;
    await startTournament(t.id);
    starting = false;
  }
</script>

<div class="row" class:registered={isRegistered} class:odd={index % 2 === 1}>
  <div class="cell when">
    <Clock size={12} strokeWidth={2.5} />
    <div class="when-text">
      <span class="primary">{timeLabel.primary}</span>
      {#if timeLabel.secondary}<span class="secondary">{timeLabel.secondary}</span>{/if}
    </div>
  </div>

  <div class="cell name-cell">
    <span class="name">{t.name || `Torneio ${t.format.toUpperCase()}`}</span>
    <span class="meta">
      <!-- Capacidade (t.size) e safe de mostrar — fixa, nao delata bots.
           O que continua ESCONDIDO e a contagem de inscritos (1/32).
           Felipe 2026-06-12: "sinalizar quantas pessoas e no campeonato". -->
      <span class="modality">{t.modality} · {t.format} · {t.size} vagas</span>
      {#if t.buy_in_cents > 0}
        <span class="buyin">R$ {(t.buy_in_cents / 100).toFixed(2)}</span>
      {:else}
        <span class="free">FREE</span>
      {/if}
    </span>
  </div>

  <div class="cell inscritos">
    <!-- Sem contagem X/Y pra ninguem (nem admin) — delata bots na demo.
         Admin ve numeros so no modal de detalhe. Felipe 2026-05-31. -->
    {#if isRegistered}
      <span class="status-pill registered">✓ INSCRITO</span>
    {:else if isFull}
      <span class="status-pill full">CHEIO</span>
    {:else}
      <span class="status-pill open">ABERTO</span>
    {/if}
  </div>

  <div class="cell actions">
    <button
      class="reg-btn"
      class:cancel={isRegistered}
      onclick={toggleReg}
      disabled={busy || (isFull && !isRegistered)}
    >
      {#if busy}
        ...
      {:else if isRegistered}
        <span class="full">CANCELAR</span>
        <span class="short">SAIR</span>
      {:else if isFull}
        CHEIO
      {:else}
        <span class="full">INSCREVER-SE</span>
        <span class="short">ENTRAR</span>
      {/if}
    </button>
    {#if $isAdmin && t.status === 'scheduled'}
      <button class="start-btn" onclick={handleStart} disabled={starting} title="Iniciar agora (admin)">
        {#if starting}...{:else}<Play size={11} strokeWidth={2.5} fill="currentColor" />{/if}
      </button>
    {/if}
  </div>
</div>

<style>
  .row {
    display: grid;
    grid-template-columns: 90px 1fr 90px 140px;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    background: #faf5ec; /* creme principal */
    color: #0D47A1; /* texto escuro roxo */
    border-left: 2px solid #d9c9a8;
    border-right: 2px solid #d9c9a8;
    transition: all 0.15s ease;
    position: relative;
  }
  .row.odd {
    background: #fff;
  }
  .row:hover {
    background: #f0e7d5;
    transform: scale(1.005);
    z-index: 1;
    box-shadow: 0 2px 8px rgba(21, 101, 192, 0.15);
  }
  .row.registered {
    background: linear-gradient(90deg, rgba(76, 175, 80, 0.12), rgba(76, 175, 80, 0.04));
    border-left-color: #4caf50;
  }

  .cell { min-width: 0; }

  /* INICIO */
  .when {
    display: flex;
    align-items: center;
    gap: 5px;
    color: #1565C0;
  }
  .when-text {
    display: flex;
    flex-direction: column;
    line-height: 1.1;
  }
  .primary {
    font-weight: 800;
    font-size: 13px;
    color: #0D47A1;
  }
  .secondary {
    font-size: 10px;
    color: #1976D2;
    font-weight: 600;
  }

  /* NOME */
  .name-cell {
    display: flex;
    flex-direction: column;
    gap: 3px;
    overflow: hidden;
  }
  .name {
    font-weight: 800;
    font-size: 13px;
    color: #0D47A1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .meta {
    display: flex;
    gap: 8px;
    font-size: 10px;
    align-items: center;
  }
  .modality {
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #1976D2;
    font-weight: 600;
  }
  .free {
    color: #2e7d32;
    font-weight: 800;
    background: rgba(76, 175, 80, 0.15);
    padding: 1px 6px;
    border-radius: 4px;
  }
  .buyin {
    color: #c62828;
    font-weight: 800;
  }

  /* INSCRITOS */
  .inscritos {
    display: flex;
    align-items: center;
    gap: 5px;
    color: #1565C0;
    justify-content: center;
    font-weight: 700;
  }
  .inscritos .count {
    font-size: 13px;
    color: #0D47A1;
  }
  /* Pill de status pro player comum (substitui contagem X/Y).
     Esconde regCount pra nao delatar quantos bots tem inscritos. */
  .status-pill {
    font-size: 10px;
    font-weight: 900;
    letter-spacing: 1px;
    padding: 4px 10px;
    border-radius: 999px;
    text-transform: uppercase;
    white-space: nowrap;
  }
  .status-pill.registered {
    background: linear-gradient(180deg, #66bb6a, #43a047);
    color: #fff;
    box-shadow: 0 1px 0 #1b5e20;
  }
  .status-pill.open {
    background: rgba(21, 101, 192, 0.12);
    color: #0D47A1;
    border: 1.5px solid rgba(21, 101, 192, 0.4);
  }
  .status-pill.full {
    background: rgba(198, 40, 40, 0.12);
    color: #c62828;
    border: 1.5px solid rgba(198, 40, 40, 0.4);
  }

  /* ACAO - botao puffy verde estilo cartoon */
  .actions {
    display: flex;
    gap: 6px;
    justify-content: flex-end;
    align-items: center;
  }
  .reg-btn {
    background: linear-gradient(180deg, #66bb6a 0%, #43a047 50%, #2e7d32 100%);
    color: #fff;
    border: 2px solid #1b5e20;
    padding: 8px 14px;
    border-radius: 8px;
    font-weight: 900;
    font-size: 10px;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    cursor: pointer;
    white-space: nowrap;
    min-width: 100px;
    box-shadow:
      0 3px 0 #1b5e20,
      inset 0 1px 0 rgba(255, 255, 255, 0.35);
    text-shadow: 0 1px 0 rgba(0, 0, 0, 0.25);
    transition: all 0.1s ease;
  }
  .reg-btn:hover {
    background: linear-gradient(180deg, #81c784 0%, #66bb6a 50%, #43a047 100%);
    transform: translateY(-1px);
    box-shadow:
      0 4px 0 #1b5e20,
      inset 0 1px 0 rgba(255, 255, 255, 0.4);
  }
  .reg-btn:active {
    transform: translateY(2px);
    box-shadow:
      0 1px 0 #1b5e20,
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
  }
  .reg-btn.cancel {
    background: linear-gradient(180deg, #ef5350 0%, #e53935 50%, #c62828 100%);
    border-color: #b71c1c;
    box-shadow:
      0 3px 0 #b71c1c,
      inset 0 1px 0 rgba(255, 255, 255, 0.35);
  }
  .reg-btn.cancel:hover {
    background: linear-gradient(180deg, #ef5350 0%, #ef5350 50%, #e53935 100%);
    box-shadow:
      0 4px 0 #b71c1c,
      inset 0 1px 0 rgba(255, 255, 255, 0.4);
  }
  .reg-btn.cancel:active {
    box-shadow:
      0 1px 0 #b71c1c,
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
  }
  .reg-btn:disabled { opacity: 0.6; cursor: not-allowed; }
  .reg-btn .full { display: inline; }
  .reg-btn .short { display: none; }

  .start-btn {
    background: linear-gradient(180deg, #ffd54f 0%, #ffb300 50%, #ff8f00 100%);
    border: 2px solid #e65100;
    color: #1565C0;
    padding: 6px 8px;
    border-radius: 6px;
    cursor: pointer;
    box-shadow:
      0 2px 0 #e65100,
      inset 0 1px 0 rgba(255, 255, 255, 0.35);
    transition: all 0.1s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .start-btn:hover { transform: translateY(-1px); }
  .start-btn:active { transform: translateY(1px); box-shadow: 0 1px 0 #e65100; }
  .start-btn:disabled { opacity: 0.6; cursor: not-allowed; }

  @media (max-width: 520px) {
    .row {
      /* 3 colunas: hora | nome (cresce) | acao. A pill de status some —
         o botao (ENTRAR/SAIR/CHEIO) ja carrega o estado. Felipe 2026-05-31:
         estava apertado demais, nome cortava em "Torneio 5...". */
      grid-template-columns: 52px 1fr auto;
      gap: 8px;
      padding: 9px 10px;
    }
    .inscritos { display: none; } /* pill redundante com o botao no mobile */
    .when { gap: 3px; }
    .primary { font-size: 12px; }
    .name { font-size: 13px; white-space: normal; line-height: 1.15; }
    .meta { font-size: 9px; gap: 4px; }
    .reg-btn {
      min-width: 0;
      font-size: 11px;
      letter-spacing: 0.6px;
      padding: 8px 12px;
    }
    .reg-btn .full { display: none; }
    .reg-btn .short { display: inline; }
  }
</style>
